import React, { useMemo, useState } from "react";

// --- ICONOS finos estilo Facebook (sin emojis) ---
const FbIconLike = () => (
  <svg
    aria-label="Me gusta"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 10V6.5a3.5 3.5 0 0 0-3.5-3.5h0L7 11v9h9.3a2 2 0 0 0 1.98-1.68l1.16-7a2 2 0 0 0-1.98-2.32H14z" />
    <path d="M7 20H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h2" />
  </svg>
);

const FbIconComment = () => (
  <svg
    aria-label="Comentar"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12c0 4.418-4.03 8-9 8-1.49 0-2.89-.29-4.12-.8L3 20l1.8-5.4C4.29 13.39 4 12.21 4 11c0-4.418 4.03-8 9-8s8 3.582 8 9z" />
  </svg>
);

const FbIconShare = () => (
  <svg
    aria-label="Compartir"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 6 22 10 18 14" />
    <path d="M3 18c3.2-6.2 7.4-8.5 12.5-8.5" />
  </svg>
);

// --- Componente ---
type Props = {
  imageUrl: string;
  caption: string;
  username?: string;
  avatarUrl?: string;
  postedAtLabel?: string; // "Ahora", "1 h", "Ayer"
  maxCaptionChars?: number; // umbral para "Ver más"
  fullWidth?: boolean; // usar todo el ancho disponible
};

const fallbackAvatar =
  "https://ui-avatars.com/api/?name=FB&background=1877F2&color=FFF&size=128";

// límites suaves para no destrozar el layout (aprox. Facebook no recorta: muestra ratio real)
const MIN_RATIO = 0.5; // 1:2 (muy vertical)
const MAX_RATIO = 3;   // 3:1 (muy apaisado)
const clampRatio = (r: number) => Math.min(MAX_RATIO, Math.max(MIN_RATIO, r));

const FacebookPostPreview: React.FC<Props> = ({
  imageUrl,
  caption,
  username = "Usuario Facebook",
  avatarUrl,
  postedAtLabel = "Ahora",
  maxCaptionChars = 180,
  fullWidth = false,
}) => {
  console.log(`[DEBUG] FacebookPostPreview - imageUrl:`, imageUrl);
  const [expanded, setExpanded] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [containerRatio, setContainerRatio] = useState<number>(1); // por defecto cuadrado hasta cargar

  const handleImageError = () => {
    console.error(`[DEBUG] Error cargando imagen en Facebook:`, imageUrl);
    setImgOk(false);
  };

  const handleImageLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
    console.log(`[DEBUG] Imagen cargada exitosamente en Facebook:`, imageUrl);
    const img = e.currentTarget;
    const w = img.naturalWidth || 0;
    const h = img.naturalHeight || 0;
    if (w > 0 && h > 0) {
      setContainerRatio(clampRatio(w / h));
    } else {
      setContainerRatio(1);
    }
    setImgOk(true);
  };

  // Convertir \n a saltos de línea reales
  const processedCaption = useMemo(() => {
    return (caption || "").replace(/\\n/g, '\n');
  }, [caption]);

  const needsClamp = (processedCaption ?? "").length > maxCaptionChars;
  const shortCaption = useMemo(() => {
    if (!needsClamp) return processedCaption;
    return (processedCaption || "").slice(0, maxCaptionChars).trimEnd() + "…";
  }, [processedCaption, needsClamp, maxCaptionChars]);

  return (
    <div
      className={`w-full ${fullWidth ? '' : 'max-w-md mx-auto'} bg-white rounded-2xl border border-gray-200`}
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <img
            src={avatarUrl || fallbackAvatar}
            onError={(e) => {
              (e.target as HTMLImageElement).src = fallbackAvatar;
            }}
            alt={`${username} avatar`}
            className="w-9 h-9 rounded-full object-cover"
          />
          <div className="leading-tight">
            <span className="text-sm font-semibold">Cuenta de {username}</span>
            <div className="text-xs text-gray-500">{postedAtLabel}</div>
          </div>
        </div>
        <button aria-label="Opciones" className="text-gray-500 hover:text-gray-700">
          •••
        </button>
      </div>

      {/* Caption con "Ver más" */}
      <div className="px-4 pb-3">
        <p className="text-sm whitespace-pre-wrap break-words">
          {expanded || !needsClamp ? processedCaption : shortCaption}{" "}
          {needsClamp && !expanded && (
            <button
              type="button"
              className="text-gray-600 hover:text-blue-600 font-medium"
              onClick={() => setExpanded(true)}
            >
              Ver más
            </button>
          )}
        </p>
      </div>

      {/* Media (respeta el ratio real como en Facebook) */}
      <div className="bg-gray-100">
        <div
          className="relative w-full overflow-hidden"
          style={{
            aspectRatio: String(containerRatio),
            // límite de altura para que no sea interminable en la preview
            maxHeight: "80vh",
          }}
        >
          {imgOk ? (
            <img
              src={imageUrl}
              alt="Post"
              className="absolute inset-0 w-full h-full object-cover"
              onError={handleImageError}
              onLoad={handleImageLoad}
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              <span className="text-sm">No se pudo cargar la imagen</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-around px-4 py-2 border-t border-gray-200">
        <button
          className="flex items-center gap-2 text-gray-700 hover:text-blue-600 text-sm font-semibold"
          aria-label="Me gusta"
        >
          <FbIconLike />
          <span>Me gusta</span>
        </button>
        <button
          className="flex items-center gap-2 text-gray-700 hover:text-blue-600 text-sm font-semibold"
          aria-label="Comentar"
        >
          <FbIconComment />
          <span>Comentar</span>
        </button>
        <button
          className="flex items-center gap-2 text-gray-700 hover:text-blue-600 text-sm font-semibold"
          aria-label="Compartir"
        >
          <FbIconShare />
          <span>Compartir</span>
        </button>
      </div>
    </div>
  );
};

export default FacebookPostPreview;
